import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { BizEntityStateStoreService, BizEntityTypeStateStoreService, EntityBottomOffCanvasService, EntityLinkService, IXappCapResponse } from '@optima-stride-ai/optima-app-framework';
import { AppStateService } from '../state/app-state';

/**
 * Owns the "open this app via a shared entity link" flow: decoding an incoming xappCap (from the
 * URL on load, or postMessage'd by an embedded cross-app iframe like VaiC's chat), fetching that
 * entity directly by id, and opening its detail off-canvas on top of whatever route the user is
 * currently on - independent of any explorer's pagination/lazy-loaded dataset, so it works
 * regardless of whether that entity's folder was ever visited. Call `init()` once on app bootstrap
 * (see app-shell.component.ts) and `destroy()` on teardown.
 */
@Injectable({ providedIn: 'root' })
export class LinkNavigationService {
  private readonly router = inject(Router);
  private readonly appStateService = inject(AppStateService);
  private readonly entityLinkService = inject(EntityLinkService);
  private readonly bizEntityTypeStateStoreService = inject(BizEntityTypeStateStoreService);
  private readonly bizEntityStateStoreService = inject(BizEntityStateStoreService);
  private readonly entityBottomOffCanvasService = inject(EntityBottomOffCanvasService);

  /**
   * Opens the entity's detail view when this app is opened via a shared entity link (e.g.
   * `${origin}/<any-route>?entityLink=...`, generated by the "Copy link..." context menu action),
   * and starts listening for the postMessage variant (see `onWindowMessage`). Never navigates away
   * from the current route, so it never disturbs the VaiC chat split panel. This is a same-app
   * permalink under its own `entityLink` query param, deliberately separate from the cross-app
   * `xappCap` protocol - but reuses `initFromXappCap` so a link into a different tenant still gets
   * the same tenant-switch-then-resume-after-reload handling `xappCap` links already have. (Note:
   * pre-auth capture - surviving a not-yet-logged-in redirect to login - only works once the
   * framework's capture IIFE also captures `entityLink`, which requires a framework publish.)
   */
  init(): void {
    window.addEventListener('message', this.onWindowMessage);
    const entityLinkParam = this.router.parseUrl(this.router.url).queryParamMap.get('entityLink') ?? undefined;
    const response = this.entityLinkService.resolveIncomingEntityLink(entityLinkParam);
    if (!response) {
      return;
    }
    this.clearEntityLinkFromUrl();
    this.openEntityDetail(response);
  }

  destroy(): void {
    window.removeEventListener('message', this.onWindowMessage);
  }

  /**
   * Handles an entityLink clicked *inside* the embedded VaiC (or Hayagriva) iframe shown in the
   * split panel. A plain <a> click inside that iframe can only ever navigate the iframe itself -
   * it has no way to reach this host window - so the embedded app must `postMessage` us instead of
   * navigating. This listens for that message and drives this app's own router, leaving the split
   * panel/iframe untouched. Requires the embedded app to detect it's iframed and post
   * `{ source: 'optima-entity-link', entityLink }` to this window instead of following the link.
   */
  private readonly onWindowMessage = (event: MessageEvent): void => {
    const entityLink = event.data?.entityLink;
    if (typeof entityLink !== 'string') {
      return;
    }
    const response = this.entityLinkService.resolveIncomingEntityLink(entityLink);
    if (!response) {
      return;
    }
    this.openEntityDetail(response);
  };

  /**
   * Fetches the entity directly by id (a single targeted API call, not dependent on any explorer's
   * paginated/lazy-loaded dataset) and opens its detail off-canvas on top of the current route -
   * `<app-off-canvas-collective>` lives in the app shell, so it can open regardless of route.
   */
  private openEntityDetail(response: IXappCapResponse): void {
    this.bizEntityStateStoreService.getState(this.bizEntityTypeStateStoreService, undefined, [response.id]).then((entities) => {
      const entity = entities?.find(item => String(item.id) === String(response.id));
      if (!entity?.type) {
        return;
      }
      this.appStateService.offCanvasDetailActiveFor = entity;
      this.entityBottomOffCanvasService.open(entity);
    });
  }

  /** Strips the consumed `entityLink` param so a page refresh doesn't re-trigger the same navigation. */
  private clearEntityLinkFromUrl(): void {
    this.router.navigate([], { queryParams: { entityLink: null }, queryParamsHandling: 'merge', replaceUrl: true });
  }
}
